import React, { Component } from "react";
import { withRouter, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import {
  removeSpecialCharacters,
  removeNonNumericCharacters,
  constructDebitEpxRequest,
} from "../../utils/utils";
import PaymentTypeButtonBar from "../payment-type-button-bar/payment-type-button-bar.component";
import LoanDetails from "../loan-details/loan-details.component";
import LoanPaymentDebitForm from "../forms/loan-payment-debit/loan-payment-debit-form.component";
import LoanPaymentAchForm from "../forms/loan-payment-ach/loan-payment-ach-form.component";
import { changeCurrentStep } from "../../actions/processStep.actions";
import { searchForInstitution, clearAchInstitution } from "../../actions/achInstitution.actions";
import { verifyPayment, transferFormToPaymentObj } from "../../actions/payment.actions";
import { modifyHeader, changePaymentType, toggleEditMode } from "../../actions/appStatus.actions";
import { togglePrincipalPayoffSwitch } from "../../actions/payoffCheck.actions";
import { loanPaymentHeader } from "../../utils/content";
import { scrollTo } from "../../utils/smoothScroll";
import { showModal } from "../../actions/modal.actions";
import Show from "../../utils/show.component";
import ReactGA from "react-ga";
import { change } from "redux-form";
import RecurringPaymentBox from "../recurring-payment-box/recurring-payment-box.component";

class LoanPaymentComponent extends Component {
  constructor(props) {
    super(props);
    ReactGA.pageview(
      `payments.laketrust.org${window.location.pathname}`,
      [],
      "Loan Payments - Online Payment"
    );
    // Update step state and change the header content
    this.props.changeCurrentStep(2);
    this.props.modifyHeader(loanPaymentHeader.header, loanPaymentHeader.description);
    this.submitForm = this.submitForm.bind(this);
  }

  submitForm(formName) {
    scrollTo(0, 1000);
    const formValues = this.props.form[formName].values;
    // Debit payments need to go through EPX and be verified, ACH payments just need to pass on the details
    if (this.props.appStatus.paymentType === "debit") {
      const paymentDetails = constructDebitEpxRequest(formValues);

      // Verify payment details endpoint
      this.props.verifyPayment(paymentDetails);
    } else {
      // ACH payments
      // Format amount and expiration date fields for EPX
      let paymentDetails = Object.assign({}, formValues);

      paymentDetails.Amount = removeNonNumericCharacters(paymentDetails.Amount);
      this.props.transferFormToPaymentObj(paymentDetails);
    }

    // Turn edit mode back to false if it was toggled
    if (this.props.appStatus.editMode) {
      this.props.toggleEditMode();
    }
  }

  render() {
    // If we got back a payment error and it is blank, go to the next screen.
    if (
      Object.keys(this.props.payment).length > 0 &&
      !this.props.appStatus.editMode &&
      this.props.payment.PaymentError === "" &&
      this.props.payoffCheck.PaymentIsAllowed
    ) {
      return <Redirect to="/loan-review" />;
    }
    // If there is some kind of error that requires the error page, send them there.
    if (this.props.appStatus.errorPage) {
      return <Redirect to="/payment-unavailable" />;
    }
    return (
      <div className="container app-container" data-e2e-id="paymentComponent">
        {/* Hit endpoint and pass in loan details as props once we have them */}
        <LoanDetails
          description={this.props.loans.Description}
          loanNumber={this.props.loans.LoanNumber}
          nextDueDate={this.props.loans.NextDueDate}
          amountDue={this.props.loans.AmountDue}
          nextNextDueDate={this.props.loans.NextNextDueDate}
          regularMinimumPaymentAmount={this.props.loans.RegularMinimumPaymentAmount}
          amountPastDue={this.props.loans.AmountPastDue}
          showModal={this.props.showModal}
          ChargeOffIsRequired={this.props.loans.ChargeOffIsRequired}
          showRegularAmountWarning={this.props.loans.ShowRegularAmountWarning}
        />
        {/* hide recurring payments for now */}
        <Show condition={this.props.loans.RecurringPayment !== null && false}>
          <RecurringPaymentBox recurringPayment={this.props.loans.RecurringPayment} />
        </Show>
        <PaymentTypeButtonBar
          changePaymentType={this.props.changePaymentType}
          currentPaymentType={this.props.appStatus.paymentType}
          achAllowed={this.props.loans.AchIsAllowed}
          ChargeOffIsRequired={this.props.loans.ChargeOffIsRequired}
          achNotAllowedMessage={this.props.loans.AchMessage}
        />
        <Show condition={this.props.appStatus.paymentType === "debit"}>
          {/* Need to pass in the form values so we can check the amount in the form to display a warning */}
          <LoanPaymentDebitForm
            show={true}
            formValues={this.props.form.LoanPaymentDebit}
            amountDue={this.props.loans.AmountDue}
            submitForm={this.submitForm}
            ChargeOffIsRequired={this.props.loans.ChargeOffIsRequired}
            principalPayoffSwitch={this.props.payoffCheck.principalOnlyChangedToPayoff}
            togglePrincipalPayoffSwitch={this.props.togglePrincipalPayoffSwitch}
            change={this.props.change}
            nextDueDate={this.props.loans.NextDueDate}
          />
        </Show>
        <LoanPaymentAchForm
          maxAllowedAchAmount={this.props.loans.MaxAllowedAchAmount}
          searchForInstitution={this.props.searchForInstitution}
          clearAchInstitution={this.props.clearAchInstitution}
          show={this.props.appStatus.paymentType === "ach"}
          formValues={this.props.form.LoanPaymentAch}
          amountDue={this.props.loans.AmountDue}
          regularAmountDue={this.props.loans.RegularMinimumPaymentAmount.toFixed(2)}
          dueDate={this.props.loans.NextDueDate}
          recurringPayment={this.props.loans.RecurringPayment}
          submitForm={this.submitForm}
          showModal={this.props.showModal}
          achInstitution={this.props.achInstitution}
          nameOnAccount={this.props.loans.MemberName}
          principalPayoffSwitch={this.props.payoffCheck.principalOnlyChangedToPayoff}
          togglePrincipalPayoffSwitch={this.props.togglePrincipalPayoffSwitch}
          change={this.props.change}
          nextDueDate={this.props.loans.NextDueDate}
        />
      </div>
    );
  }
}

// Map global store to component props
const mapStateToProps = (state) => state;

// Hook up actions to component
const mapDispatchToProps = {
  changeCurrentStep,
  verifyPayment,
  modifyHeader,
  changePaymentType,
  searchForInstitution,
  clearAchInstitution,
  transferFormToPaymentObj,
  toggleEditMode,
  showModal,
  togglePrincipalPayoffSwitch,
  change,
};

//Connect to Redux store
const LoanPayment = withRouter(connect(mapStateToProps, mapDispatchToProps)(LoanPaymentComponent));

export default LoanPayment;



// WEBPACK FOOTER //
// ./src/components/loan-payment/loan-payment.component.js